/**
 * Meta Pixel (Facebook Pixel) helper — GDPR consent-gated.
 *
 * The base snippet is loaded in /app/frontend/public/index.html which
 * IMMEDIATELY revokes consent after init. Nothing is sent to Facebook
 * until CookieConsentBanner calls `window.metaPixelGrantConsent()`.
 *
 * This helper wraps `window.fbq` with a safe no-op fallback (never
 * throws, never crashes UI) and exposes React-friendly Standard Event
 * shortcuts.
 *
 * Meta Standard Events used:
 *   - PageView             (route changes; fired by useMetaPixelPageTracking)
 *   - Lead                 (calculator email opt-in)
 *   - Contact              (contact form submit)
 *   - CompleteRegistration (portal user created)
 *   - Purchase             (Barion success — value + currency)
 *   - Schedule             (consultation / session booked)
 *   - ViewContent          (program / blog article view)
 */

const isClient = () => typeof window !== 'undefined';

const safeFbq = (...args) => {
  if (!isClient()) return;
  try {
    if (typeof window.fbq !== 'function') return;
    if (!window.meta_pixel_id) return;              // no ID → no-op
    if (!window.__META_PIXEL_CONSENT_GRANTED__) return; // consent gate
    window.fbq(...args);
  } catch (_e) {
    /* never crash UI on tracking failure */
  }
};

export const isMetaPixelConfigured = () =>
  isClient() && typeof window.fbq === 'function' && !!window.meta_pixel_id;

export const isMetaPixelConsented = () =>
  isClient() && !!window.__META_PIXEL_CONSENT_GRANTED__;

/** Standard PageView — used by useMetaPixelPageTracking on route change. */
export const trackPageView = () => safeFbq('track', 'PageView');

/** Lead — free lead capture (calculator email opt-in etc). */
export const trackLead = ({ content_name, value = 0, currency = 'HUF' } = {}) => {
  safeFbq('track', 'Lead', { content_name, value, currency });
};

/** Contact — user pressed submit on a contact form. */
export const trackContact = ({ content_name } = {}) => {
  safeFbq('track', 'Contact', { content_name });
};

/** CompleteRegistration — portal user account was successfully created. */
export const trackCompleteRegistration = ({ content_name, status = 'success' } = {}) => {
  safeFbq('track', 'CompleteRegistration', { content_name, status });
};

/** Purchase — Barion returned Succeeded. `value` in HUF, no currency conversion. */
export const trackPurchase = ({ value, currency = 'HUF', content_ids = [], content_name } = {}) => {
  safeFbq('track', 'Purchase', {
    value:    Number(value) || 0,
    currency,
    content_ids,
    content_name,
    content_type: 'product',
  });
};

/** Schedule — consultation or session booked. */
export const trackSchedule = ({ content_name } = {}) => {
  safeFbq('track', 'Schedule', { content_name });
};

/** ViewContent — user opened a program or blog article. */
export const trackViewContent = ({ content_name, content_ids = [], content_type = 'product' } = {}) => {
  safeFbq('track', 'ViewContent', { content_name, content_ids, content_type });
};

/** Fallback custom event helper if we ever need one outside the Standard set. */
export const trackCustom = (eventName, params = {}) => {
  safeFbq('trackCustom', eventName, params);
};
